import dayjs from 'dayjs';
import Icon from 'components/Icon/Icon';
import useWindowSize from 'common/hooks/useWindowSize';
import { Establishment } from 'types/Establishment';
import { Calendar as BigCalendar, dayjsLocalizer, Views, NavigateAction } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { Event } from 'types/Event';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CustomToolbar from './CustomToolbar';
import { getEventColors } from './EventColors';
import { calculateDateRangeForView } from './calendarUtils';
import { EventListView } from './EventListView';
import { parseDateInParis, formatDateInParis } from '../../utils/dateUtils';

// Set French locale
dayjs.locale('fr');

const localizer = dayjsLocalizer(dayjs);
const showDateRange = true;

const DragAndDropCalendar = withDragAndDrop(BigCalendar);

interface SlotInfo {
  start: Date;
  end: Date;
}

interface ActiveViews {
  month: boolean;
  week: boolean;
  day: boolean;
  agenda: boolean;
}

// Interface pour l'état du calendrier
interface CalendarState {
  currentDate: Date;
  currentView: 'month' | 'week' | 'day' | 'agenda';
  events: Event[];
  backgroundEvents: Event[];
  dateRange: { start: Date; end: Date };
}

interface CalendarProps {
  activeViews: ActiveViews;
  defaultView?: 'month' | 'week' | 'day' | 'agenda';
  events: Event[];
  backgroundEvents?: Event[];
  selectable?: boolean;
  onSelectSlot?: (slotInfo: SlotInfo) => void;
  onSelectEvent?: (event: Event) => void;
  onRangeChange?: (range: Date[] | { start: Date; end: Date }) => void;
  onNavigate?: (newDate: Date, view?: Views, action?: NavigateAction) => void;
  date?: Date;
  style?: React.CSSProperties;
  onEventDrop?: (args: any) => void;
  onEventResize?: (args: any) => void;
  resizable?: boolean;
  draggable?: boolean;
}

function Calendar({
  defaultView = 'month',
  events,
  backgroundEvents = [],
  selectable = false,
  onSelectSlot,
  onSelectEvent,
  onRangeChange,
  onNavigate,
  date,
  activeViews,
  style,
  onEventDrop,
  onEventResize,
  resizable = onEventResize !== undefined,
  draggable = onEventDrop !== undefined,
}: CalendarProps) {
  const { isMobile, isTablet } = useWindowSize();
  const { t } = useTranslation();

  // Initialiser l'état du calendrier
  const [calendarState, setCalendarState] = useState<CalendarState>({
    currentDate: date || new Date(),
    currentView: defaultView,
    events: events || [],
    backgroundEvents: backgroundEvents || [],
    dateRange: {
      start: parseDateInParis(dayjs().startOf('month').toDate()).toDate(),
      end: parseDateInParis(dayjs().endOf('month').toDate()).toDate(),
    },
  });

  // Mettre à jour l'état interne lorsque les props changent
  useEffect(() => {
    // Ne pas mettre à jour si les props sont undefined ou null
    // Cela évite les boucles infinies de rendu
    if (events === undefined && backgroundEvents === undefined && date === undefined) return;

    // Utiliser une fonction pour éviter d'ajouter calendarState comme dépendance
    setCalendarState((prevState) => {
      // Vérifier si les valeurs ont réellement changé pour éviter les rendus inutiles
      // Utiliser une comparaison plus précise pour les tableaux d'événements
      const eventsChanged =
        events &&
        (!prevState.events ||
          events.length !== prevState.events.length ||
          JSON.stringify(events) !== JSON.stringify(prevState.events));

      const backgroundEventsChanged =
        backgroundEvents &&
        (!prevState.backgroundEvents ||
          backgroundEvents.length !== prevState.backgroundEvents.length ||
          JSON.stringify(backgroundEvents) !== JSON.stringify(prevState.backgroundEvents));

      const dateChanged =
        date && (!prevState.currentDate || date.getTime() !== prevState.currentDate.getTime());

      // Ne mettre à jour que si quelque chose a réellement changé
      if (eventsChanged || backgroundEventsChanged || dateChanged) {
        return {
          ...prevState,
          events: events || prevState.events,
          backgroundEvents: backgroundEvents || prevState.backgroundEvents,
          currentDate: date || prevState.currentDate,
        };
      }

      // Si rien n'a changé, retourner l'état précédent tel quel
      return prevState;
    });
  }, [events, backgroundEvents, date]);

  // Utiliser les événements normaux avec timezone adjustment
  const displayEvents = useMemo(
    () =>
      calendarState.events.map((e: Event) => ({
        ...e,
        start: dayjs(e.start).tz('Europe/Paris').toDate(),
        end: dayjs(e.end).tz('Europe/Paris').toDate(),
        parentStart: e.parentStart ? dayjs(e.parentStart).tz('Europe/Paris').toDate() : undefined,
        parentEnd: e.parentEnd ? dayjs(e.parentEnd).tz('Europe/Paris').toDate() : undefined,
      })),
    [calendarState.events]
  );

  // Utiliser les événements d'arrière-plan normaux avec timezone adjustment
  const displayBackgroundEvents = useMemo(
    () =>
      calendarState.backgroundEvents.map((e: Event) => ({
        ...e,
        start: dayjs(e.start).tz('Europe/Paris').toDate(),
        end: dayjs(e.end).tz('Europe/Paris').toDate(),
        parentStart: e.parentStart ? dayjs(e.parentStart).tz('Europe/Paris').toDate() : undefined,
        parentEnd: e.parentEnd ? dayjs(e.parentEnd).tz('Europe/Paris').toDate() : undefined,
      })),
    [calendarState.backgroundEvents]
  );

  // Gestionnaires d'événements internes
  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      // Calculer la plage de dates en fonction du format de retour
      let newRange: { start: Date; end: Date };

      if (Array.isArray(range)) {
        // Pour la vue mois, range est un tableau de dates (jours du mois)
        newRange = {
          start: range[0],
          end: range[range.length - 1],
        };
      } else {
        // Pour les vues semaine et jour, range est un objet {start, end}
        newRange = range;
      }

      setCalendarState((prevState) => ({
        ...prevState,
        dateRange: newRange,
      }));

      // Propager l'événement au parent si nécessaire
      if (onRangeChange) {
        onRangeChange(range);
      }
    },
    [onRangeChange]
  );

  const handleViewChange = useCallback(
    (view: Views) => {
      setCalendarState((prevState) => ({
        ...prevState,
        currentView: view as 'month' | 'week' | 'day' | 'agenda',
      }));

      // Utiliser la fonction utilitaire pour garder la logique DRY
      const range = calculateDateRangeForView(calendarState.currentDate, view as any);
      // Déclencher le changement de plage
      handleRangeChange(range);
    },
    [calendarState.currentDate, handleRangeChange]
  );

  const handleNavigate = useCallback(
    (newDate: Date, view?: Views, action?: NavigateAction) => {
      setCalendarState((prevState) => ({
        ...prevState,
        currentDate: newDate,
        currentView: (view as 'month' | 'week' | 'day' | 'agenda') || prevState.currentView,
      }));

      // Propager l'événement au parent si nécessaire
      if (onNavigate) {
        onNavigate(newDate, view, action);
      }
    },
    [onNavigate]
  );

  const handleSelectEvent = useCallback(
    (event: Event) => {
      if (onSelectEvent) {
        onSelectEvent(event);
      }
    },
    [onSelectEvent]
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      if (onSelectSlot) {
        onSelectSlot(slotInfo);
      }
    },
    [onSelectSlot]
  );

  const messages = useMemo(
    () => ({
      allDay: t('calendar.allDay'),
      previous: t('calendar.previous'),
      next: t('calendar.next'),
      today: t('calendar.today'),
      month: t('calendar.month'),
      week: t('calendar.week'),
      day: t('calendar.day'),
      agenda: t('calendar.agenda'),
      date: t('calendar.date'),
      time: t('calendar.time'),
      event: t('calendar.event'),
      showMore: (total: any) => t('calendar.showMore', { total }),
    }),
    [t]
  );

  const getColors = useCallback(
    (event: Event, outlined = false) => getEventColors({ event, outlined }),
    []
  );

  const CustomEvent = useCallback(
    ({ event, truncate = false, view }: { event: Event; truncate?: boolean; view?: Views }) => {
      const { backgroundColor, textColor, border, transparency } = getColors(event);
      const textAlign = truncate ? 'truncate' : 'text-center text-wrap max-h-full max-w-full';
      const isMonthView = view === 'month';

      // Fonction pour formater le texte des événements de disponibilité avec établissement
      const formatEstablishmentText = ({ name, department }: Establishment) => {
        const nbOfChar = isMobile ? 8 : isTablet ? 12 : 20;
        if (truncate) {
          return name.substring(0, nbOfChar);
        }
        return `${name}${department ? ` (${department.CP})` : ''}`;
      };

      return (
        <div
          className={`rounded-md px-0.5 py-[1px] flex items-center h-full w-full overflow-hidden ${
            event.isExternalEvent ? 'opacity-50' : ''
          } ${border} ${backgroundColor} ${transparency}`}
          title={
            event.eventType === 'availability'
              ? event.mediatorOnEstablishment && event.mediatorOnEstablishment?.id
                ? `${formatDateInParis(event.start, 'HH:mm')} - ${formatDateInParis(
                    event.end,
                    'HH:mm'
                  )} : ${t('agenda.availableAt', { place: event.mediatorOnEstablishment.name })}`
                : `${formatDateInParis(event.start, 'HH:mm')} - ${formatDateInParis(
                    event.end,
                    'HH:mm'
                  )} : ${t('agenda.available')}`
              : `${formatDateInParis(event.start, 'HH:mm')} - ${formatDateInParis(
                  event.end,
                  'HH:mm'
                )} : ${event.title || ''}`
          }
        >
          {event.eventType === 'availability' ? (
            event.mediatorOnEstablishment && event.mediatorOnEstablishment?.id ? (
              <div className={`text-sm ${textColor} ${textAlign}`}>
                {event.isRecurringInstance && (
                  <Icon
                    name="LinkIcon"
                    className="h-[15px] w-[15px] inline-block align-text-center mr-0.5 mb-1"
                  />
                )}
                {formatEstablishmentText(event.mediatorOnEstablishment)}
              </div>
            ) : (
              <div className={`text-sm ${textColor} ${textAlign}`}>{t('agenda.available')}</div>
            )
          ) : (
            <div
              className={`w-full flex flex-row justify-between items-center text-sm ml-1 ${textColor} ${textAlign}`}
            >
              <div className="text-left text-sm truncate">
                {event.isRecurringInstance && (
                  <Icon
                    name="LinkIcon"
                    className="h-[15px] w-[15px] inline-block align-text-center mr-0.5 mb-1"
                  />
                )}
                <span className={`${event.isExternalEvent ? 'italic' : ''}`}>{event.title}</span>
              </div>
              {isMonthView && (
                <div className="flex flex-col items-end text-xs">
                  <div className="-mt-1">
                    {formatDateInParis(event.start, 'HH[h]')}
                    <span className="hidden lg:inline">{formatDateInParis(event.start, 'mm')}</span>
                  </div>
                  {showDateRange && (
                    <div className="-mb-0.5">
                      {formatDateInParis(event.end, 'HH[h]')}
                      <span className="hidden lg:inline">{formatDateInParis(event.end, 'mm')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
    [getColors, isMobile, isTablet, t]
  ); // Dependencies used in the function

  // Composants personnalisés pour la vue agenda
  function CustomAgendaView({ customEvents, customOnSelectEvent }: any) {
    return (
      <EventListView
        events={customEvents}
        onEventSelect={(item: Event) => customOnSelectEvent(item)}
      />
    );
  }

  // Méthodes statiques requises pour la vue personnalisée
  CustomAgendaView.range = (newDate: Date) => {
    const start = parseDateInParis(newDate).startOf('month').toDate();
    const end = parseDateInParis(newDate).endOf('month').toDate();
    return { start, end };
  };

  CustomAgendaView.navigate = (newDate: Date, action: string) => {
    const dateObj = parseDateInParis(newDate);
    switch (action) {
      case 'PREV':
        return dateObj.subtract(1, 'month').toDate();
      case 'NEXT':
        return dateObj.add(1, 'month').toDate();
      default:
        return dateObj.toDate();
    }
  };

  CustomAgendaView.title = (currentDate: Date) => {
    return `Agenda: ${formatDateInParis(currentDate, 'MMMM YYYY')}`;
  };

  // Composants personnalisés pour le rendu des événements
  const components = useMemo(
    () => ({
      event: (props: any) => <CustomEvent {...props} />,
      backgroundEvent: (props: any) => <CustomEvent {...props} truncate />,
      toolbar: (props: any) => (
        <CustomToolbar {...props} activeViews={activeViews} onViewChange={handleViewChange} />
      ),
      agenda: {
        event: (props: any) => <EventListView {...props} />,
      },
      month: {
        event: (props: any) => <CustomEvent truncate event={props.event} view="month" />,
      },
      week: {
        // Composant d'en-tête personnalisé pour la vue semaine qui empêche le drill-down
        header: (props: any) => {
          const { label } = props;

          // Extraire les différentes parties du label (ex: "Lundi 24")
          const parts = label.split(' ');
          const dayName = parts[0];
          const dayNumber = parts.length > 1 ? parts[1] : '';

          return (
            <div className="rbc-header" onClick={(e) => e.stopPropagation()}>
              <span className="flex flex-col items-center">
                <span className="hidden lg:inline">{dayName}</span>
                <span className="hidden md:inline lg:hidden">{dayName.substring(0, 6)}</span>
                <span className="inline md:hidden">{dayName.substring(0, 3)}</span>
                {dayNumber && <span className="text-xs">{dayNumber}</span>}
              </span>
            </div>
          );
        },
        event: (props: any) => <CustomEvent truncate event={props.event} view="week" />,
      },
    }),
    [CustomEvent, activeViews, handleViewChange]
  );

  // Propriétés pour le calendrier
  const calendarProps = useMemo(() => {
    // Format definitions
    const formats = {
      agendaDateFormat: (currentDate: Date) => formatDateInParis(currentDate, 'dddd D MMMM'),
      agendaTimeFormat: (currentDate: Date) => formatDateInParis(currentDate, 'HH:mm'),
      agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
        `${formatDateInParis(start, 'HH:mm')} - ${formatDateInParis(end, 'HH:mm')}`,
      timeGutterFormat: 'HH:mm',
      eventTimeRangeFormat: () => '',
      dayFormat: 'dddd D',
      dayHeaderFormat: 'dddd D MMMM',
      monthHeaderFormat: (currentDate: Date) => formatDateInParis(currentDate, 'MMMM YYYY'),
      selectRangeFormat: ({ start, end }: any) =>
        `${formatDateInParis(start, 'HH:mm')} - ${formatDateInParis(end, 'HH:mm')}`,
      eventTimeFormat: 'HH:mm',
      dateFormat: 'D',
    };

    // Views configuration
    const views = {
      month: activeViews.month,
      week: activeViews.week,
      day: activeViews.day,
      agenda: activeViews.agenda ? CustomAgendaView : false,
    };

    // Time boundaries
    const min = new Date(2023, 0, 1, 7, 0, 0); // Start at 7:00
    const max = new Date(2023, 0, 1, 20, 30, 0); // End at 20:30

    // Event accessors - dates are already converted to Europe/Paris in display mapping
    const startAccessor = (event: Event) => event.start;
    const endAccessor = (event: Event) => event.end;
    const titleAccessor = (event: Event) => {
      // Format personnalisé pour le titre au survol
      const startTime = formatDateInParis(event.start, 'HH:mm');
      const endTime = formatDateInParis(event.end, 'HH:mm');
      const timeRange = `${startTime} - ${endTime}`;

      if (event.eventType === 'availability') {
        if (event.mediatorOnEstablishment && event.mediatorOnEstablishment?.id) {
          const establishment = event.mediatorOnEstablishment.name;
          return `${timeRange}: ${t('agenda.availableAt', { place: establishment })}`;
        }
        return `${timeRange}: ${t('agenda.available')}`;
      }

      return `${timeRange}: ${event.title || ''}`;
    };

    return {
      localizer,
      startAccessor,
      endAccessor,
      titleAccessor,
      culture: 'fr',
      messages,
      views,
      defaultView,
      selectable,
      onSelectSlot: handleSelectSlot,
      onSelectEvent: handleSelectEvent,
      onView: handleViewChange,
      onNavigate: handleNavigate,
      onRangeChange: handleRangeChange,
      components,
      date: calendarState.currentDate,
      view: calendarState.currentView,
      style: style || { height: '100%' },
      formats,
      popup: true,
      min,
      max,
      // Optimize time slot rendering for week and day views
      step: 30, // Use 30-minute time slots instead of the default 15 minutes
      timeslots: 2, // Reduce the number of slots per step
      // Improve drag performance
      longPressThreshold: 10, // Reduce the time needed to detect a long press
    };
  }, [
    activeViews.month,
    activeViews.week,
    activeViews.day,
    activeViews.agenda,
    messages,
    defaultView,
    selectable,
    handleSelectSlot,
    handleSelectEvent,
    handleViewChange,
    handleNavigate,
    handleRangeChange,
    components,
    calendarState.currentDate,
    calendarState.currentView,
    style,
    t,
  ]);

  const dragAndDropProps = useMemo(() => {
    return {
      resizable,
      // Utiliser draggableAccessor pour contrôler si un événement peut être déplacé
      draggableAccessor: draggable ? () => true : () => false,
      // Utiliser resizableAccessor pour contrôler si un événement peut être redimensionné
      resizableAccessor: resizable ? () => true : () => false,
      // Ne pas passer onEventDrop et onEventResize si les fonctionnalités sont désactivées
      ...(draggable && onEventDrop ? { onEventDrop } : {}),
      ...(resizable && onEventResize ? { onEventResize } : {}),
    };
  }, [resizable, draggable, onEventDrop, onEventResize]);

  return (
    <DragAndDropCalendar
      {...calendarProps}
      {...dragAndDropProps}
      events={displayEvents}
      backgroundEvents={displayBackgroundEvents}
    />
  );
}

export default Calendar;
